import { inject } from "mobx-react";
import { type FunctionComponent } from "react";
import classnames from "classnames";
import { FormattedMessage } from "react-intl";

import { withAppAuth0 } from "nvent-web/services/auth0/AppAuth0Context";
import { type AppAuth0 } from "nvent-web/services/auth0/useCreateAppAuth0";

import backgroundMobile from "nvent-web/assets/webp/backgroun@mobile.webp";
import backgroundDesktop from "nvent-web/assets/webp/backgroun@desktop.webp";
import motto from "nvent-web/assets/webp/motto.webp";

import Home from "nvent-web/assets/svg/home.svg";
import Box from "nvent-web/assets/svg/box.svg";
import Logo from "nvent-web/assets/svg/logo-horizontal.svg";

import style from "./LoginPage.module.scss";

import LocalePicker from "nvent-web/scenes/Auth/components/LocalePicker";
import { FormattedHTMLMessage } from "~/nvent-web/components/FormattedHTMLMessage/FormattedHTMLMessage";
import compose from "~/nvent-web/utils/compose";
import type { UserStore } from "~/nvent-web/stores/User";

interface LoginPageInnerProps {
  auth0: AppAuth0;
  user: UserStore;
}

enum ExternalSelectionGuideLocales {
  pl = "en-pl",
  cs = "en-cz",
  en = "en-gb",
  fi = "fi-fi",
  fr = "fr-fr",
  no = "no-no",
  ru = "ru-ru",
  sv = "sv-se",
}

const LoginPageInner: FunctionComponent<LoginPageInnerProps> = ({
  auth0,
  user,
}) => {
  const { locale } = user;

  const logIn = () => {
    auth0.loginWithRedirect();
  };

  return (
    <div className={style.container}>
      <BackgroundImage />
      <div className={style.header}>
        <CornerAside position="top">
          <h1>
            <a
              href="https://www.nvent.com/raychem"
              rel="noopener noreferrer"
              target="_blank"
            >
              <img src={Logo} className={style.logo} alt="Raychem Logo" />
            </a>
          </h1>
        </CornerAside>
        <div className={style.localePicker}>
          <LocalePicker />
        </div>
      </div>
      <div className={style.main}>
        <div className={style.tilesWrapper}>
          <Tile>
            <div className={classnames(style.iconWrapper, style.red)}>
              <img src={Home} alt="Home" />
            </div>
            <div>
              <h2>
                <FormattedHTMLMessage id="login.loginSection.title" />
              </h2>
              <p>
                <FormattedMessage id="login.loginSection.description" />
              </p>
              <button className={style.red} onClick={logIn}>
                <FormattedMessage id="login.loginSection.action" />
              </button>
            </div>
          </Tile>
          <div className={style.separator}>
            <span>
              <FormattedMessage id="login.or" />
            </span>
          </div>
          <Tile>
            <div className={classnames(style.iconWrapper, style.green)}>
              <img src={Box} alt="Box" />
            </div>
            <div>
              <h2>
                <FormattedHTMLMessage id="login.selectionSection.title" />
              </h2>
              <p>
                <FormattedMessage id="login.selectionSection.description" />
              </p>
              <a
                href={
                  "https://raychem.nvent.com/" +
                  ExternalSelectionGuideLocales[locale] +
                  "/resources/technology/floor-heating-product-selection-guide"
                }
                target="_blank"
                rel="noopener noreferrer"
                className={style.green}
              >
                <FormattedMessage id="login.selectionSection.action" />
              </a>
            </div>
          </Tile>
        </div>
      </div>
      <div className={style.footer}>
        <CornerAside position="bottom">
          <img
            src={motto}
            className={style.motto}
            alt="Excellence is everything"
          />
        </CornerAside>
      </div>
    </div>
  );
};

const BackgroundImage = () => (
  <img
    className={style.backgroundImage}
    src={backgroundDesktop}
    srcSet={`${backgroundMobile} 640w, ${backgroundDesktop}`}
    alt="Background"
  />
);

const CornerAside = ({
  position,
  children,
}: {
  position: "top" | "bottom";
  children?: React.ReactNode;
}) => (
  <div
    className={classnames(
      style.cornerAside,
      position === "top" ? style.cornerAsideTop : style.cornerAsideBottom
    )}
  >
    {children}
  </div>
);

const Tile = ({ children }: { children: React.ReactNode }) => (
  <section className={style.tile}>{children}</section>
);

export const LoginPage = compose(withAppAuth0, inject("user"))(LoginPageInner);

export default LoginPage;
